import { Theme } from '@material-ui/core/styles';
import { SimplePaletteColorOptions } from '@material-ui/core/styles/createPalette';
import { capitalCase } from 'change-case';
import { DateRange, guid, TimestampRange, UTC } from 'rewire-common';
import { arraySearch, error, validator } from 'rewire-ui';

export const DeletedScopeId = '00000000-0000-0000-0000-000000000000';
export const ArchivedScopeId = '11111111-1111-1111-1111-111111111111';

export const ProductName: string = 'WorkSight Flow';

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: T[P] extends Array<infer U>
        ? Array<DeepPartial<U>>
        : T[P] extends ReadonlyArray<infer U>
          ? ReadonlyArray<DeepPartial<U>>
          : DeepPartial<T[P]>;
    }
  : T;

export interface IResult<T> {
  took: number;
  totalCount: number;
  cursor?: string;
  data: T[];
}

export type OperationType = 'updated' | 'added' | 'deleted';
export interface ISubscriptionRecord<T> {
  [s: string]: T | OperationType; // Add the 'OperationType' as a valid type for the index property
  operation: OperationType;
}

export interface ISubscriptionBatchedRecord<T> {
  timestamp: string;
  records: ISubscriptionRecord<T>[];
}

export interface IEntity {
  id: string;
}

export interface ISelectableEntity extends IEntity {
  isSelected: boolean;
}

export interface INameEntity extends IEntity {
  code?: string;
  name: string;
  tags?: string[];
  fields?: Record<string, any>;
}

export interface INameLike {
  id: string;
  name: string;
}

export interface IOrganization extends INameEntity {
  locations: IResult<ILocation>;
  users: IResult<IUser>;
}

export interface IState {
  id: string;
  code?: string;
  name: string;
}

export interface ICountry {
  id: string;
  code?: string;
  name: string;
}

export interface ITimeZone {
  id: string;
  name: string;
}

export type ITimeFormat = 'military' | 'uppercaseAMPM' | 'lowercaseAMPM';
export type IWeekDay = 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';
export enum IWeekDayNum {
  Sunday,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
}

export interface ILocation extends INameEntity {
  organization: IOrganization;
  country: ICountry;
  state: IState;
  tz: ITimeZone;
  weekStart: IWeekDay;
  timeFormat: ITimeFormat;
  postedDate?: UTC;
  workflow: IWorkflow;
  timeOffrequestWorkflow: IWorkflow;
  udfs: IResult<IUdf>;
  areas: IResult<IArea>;
  users: IResult<IUser>;
  planAdministrator?: IUser;
  subscriptionCount: number;
  employeeCount: number;
  activeUsers: number;
  activeEmployees: number;
  tier: Tier;
  version: string;
  subscriptionExpires: UTC;
  modules?: string[];
}

export interface IFavourite {
  id: string;
  name: string;
  path: string;
}

export type AuditLogEntryType = 'employee' | 'employee_timecard' | 'timecard' | 'timeoff' | 'user' | 'role';
export enum AuditLogEntryTypes {
  Employee = 'employee',
  EmployeeTimecard = 'employee_timecard',
  Timecard = 'timecard',
  TimeOff = 'timeoff',
  User = 'user',
  Role = 'role',
}
export const _auditLogEntryTypeMap = {
  employee: 'Employee',
  employee_timecard: 'Timecard Status',
  timecard: 'Timecard',
  timeoff: 'Time Off',
  user: 'User',
  role: 'Role',
};

export const _auditLogEntryTypeToUdfTypeMap: Record<string, string> = {
  employee: 'employees',
  timecard: 'timecards',
  user: 'users',
};

export type AuditLogEntryOperation = 'added' | 'deleted' | 'archived' | 'unarchived' | 'modified';
export enum AuditLogEntryOperations {
  Added = 'added',
  Deleted = 'deleted',
  Archived = 'archived',
  Unarchived = 'unarchived',
  Modified = 'modified',
}
export const _auditLogEntryOperationMap = {
  added: 'Added',
  deleted: 'Deleted',
  archived: 'Archived',
  unarchived: 'Unarchived',
  modified: 'Modified',
};

export interface IAuditLog {
  id: string;
  location: ILocation;
  effective: UTC;
  type: AuditLogEntryType;
  operation: AuditLogEntryOperation;
  description: string;
  entityId: string;
  transactionId?: string;
  delta?: Record<string, unknown>;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  createdBy?: string;
  createdDate: UTC;
  isSelected: boolean;
}

export interface AuditLogFilterInput {
  ids?: string[];
  effective?: DateRange;
  types?: AuditLogEntryType[];
  operations?: AuditLogEntryOperation[];
  description?: string;
  entityIds?: string[];
  transactionIds?: string[];
  createdBy?: string[];
  createdDate?: DateRange;
  custom?: FieldFilter[];
}
export interface AuditLogSortInput extends ISortCustom {
  type?: SortInputPriority;
  operation?: SortInputPriority;
  createdBy?: SortInputPriority;
  createdDate?: SortInputPriority;
  transactionId?: SortInputPriority;
}

export interface IArea extends INameEntity {
  location: ILocation;
  organization: IOrganization;
  groups: IGroup[];
}

export interface ISimplifiedBatch extends INameEntity {
  status: BatchStatusType;
}

export interface IEmployeeInfo {
  employeeId: string;
  earnedDate: UTC;
}

export interface IBatch extends INameEntity {
  status: BatchStatusType;
  dateCreated: UTC;
  createdBy: string;
  timecardCount: number;
  errors?: string;
  exportFileUrl?: string;
  exportStatus?: string;
  // isActive?:     boolean;
}

export interface ICommand {
  command: string;
  data?: Record<string, any>;
  context?: Record<string, any>;
}

export interface IBatchDashboard {
  timecardsNotInBatch: number;
  timecardsInBatch: number;
  timecardsInError: number;
  timecardsInNone: number;
  timecardsInSignedOff: number;
  timecardsInApproved: number;
}

export interface IWorkflow {
  id: string;
  name: string;
  finalState: string;
}

export interface ITimecardWorkflow extends IWorkflow {
  fields?: Record<string, unknown>;
}

export type ViewType = 'view' | 'schedulingRule';

export type EntityType =
  | 'employees'
  | 'batches'
  | 'areas'
  | 'groups'
  | 'occupations'
  | 'reasons'
  | 'generators'
  | 'templates'
  | 'users'
  | 'categories'
  | 'documents'
  | 'views'
  | 'timeOffTypes'
  | 'timezones'
  | 'countries'
  | 'states'
  | 'roles'
  | 'applicationTemplates'
  | 'all';

export interface IFilter {
  id?: string[];
  code?: string[];
  name?: string[];
  tags?: string[];
  search?: string;
  custom?: FieldFilter[];
}
export interface FieldBetweenFilterValue {
  lower?: any;
  upper?: any;
}
export interface FieldIntersectsFilterValue {
  lowerField: string;
  upperField: string;
  lower?: any;
  upper?: any;
}
export interface FieldContainsFilterValue {
  lowerField: string;
  upperField: string;
  value: any;
}
export interface FieldRawFilterValue {
  value: Record<string, unknown>;
}
export interface IDashboardGuestToken {
  id: string;
  token: string;
}
export interface ISortCustom {
  custom?: SortInput[];
}
export interface ISort extends ISortCustom {
  code?: SortInputPriority;
  name?: SortInputPriority;
}
export type SortDirection = 'asc' | 'desc';
export interface SortInput {
  field: string;
  direction?: SortDirection;
  priority?: number;
}
export interface SortInputPriority {
  direction?: SortDirection;
  priority?: number;
}

export interface UserFilter extends IFilter {
  email?: string[];
  locationId?: string[];
}
export interface UserSort extends ISort {
  email?: SortInputPriority;
}

export interface ResponseFilter {
  effective?: DateRange;
  employeeIds?: string[];
  groupIds?: string[];
  occupationIds?: string[];
  generatorIds?: string[];
  templateIds?: string[];
  types?: IResponseType[];
}

export type OrganizationFilter = IFilter;
export type OrganizationSort = ISort;

export type LocationFilter = IFilter;
export type LocationSort = ISort;

export type AreaFilter = IFilter;
export interface AreaSort extends ISort {
  id?: SortInputPriority;
}

export interface GroupFilter extends IFilter {
  areaId?: string[];
  position?: string[];
}
export interface GroupSort extends ISort {
  id?: SortInputPriority;
  position?: SortInputPriority;
}

export interface EmployeeFilter extends IFilter {
  email?: string[];
}
export interface EmployeeSort extends ISort {
  email?: SortInputPriority;
}

export type GeneratorFilter = IFilter;
export type GeneratorSort = ISort;

export type OccupationFilter = IFilter;
export type OccupationSort = ISort;

export interface ReasonFilter extends IFilter {
  category?: string[];
}
export interface ReasonSort extends ISort {
  category?: SortInputPriority;
}

export type TimeOffTypeFilter = IFilter;
export type TimeOffTypeSort = ISort;

export type TemplateFilter = IFilter;
export type TemplateSort = ISort;

export interface ViewFilter extends IFilter {
  type: ViewType;
}
export type ViewSort = ISort;

export interface ITypeBase {
  kind: string;
  name: string;
  description?: string;
}

export interface ITypeArgs {
  name: string;
  description?: string;
  defaultValue?: string;
  type: ITypeInfo;
}

export interface ITypeField extends ITypeBase {
  args?: ITypeArgs[];
  type?: ITypeInfo;
}

export interface ITypeInfo extends ITypeBase {
  fields?: ITypeField[];
  interfaces?: ITypeInfo[];
  ofType?: ITypeInfo;
}

export interface TimecardFilterInput {
  employeeIds?: string[];
  occupationsIds?: string[];
  tags?: string[];
  groupIds?: string[];
  areaIds?: string[];
  adjustments?: boolean;
  reasonIds?: string[];
  overtimeReasonIds?: string[];
  batchIds?: string[];
  createdBy?: string;
  search?: string;
  custom?: FieldFilter[];
}

export interface TimecardFilterSubscriptionInput {
  ids?: string[];
  templateIds?: string[];
  reasonIds?: string[];
  overtimeReasonIds?: string[];
  createdBy?: string;
  jobIds?: string[];
  groupIds?: string[];
  employeeIds?: string[];
  occupationsIds?: string[];
  effective?: DateRange;
}

export interface TimecardTestFilterInput extends IFilter {
  ids?: string[];
  status?: TimecardTestStatusType;
  areaIds?: string[];
  groupIds?: string[];
  employeeIds?: string[];
  occupationsIds?: string[];
  generatorIds?: string[];
  lastRanAfter?: UTC;
  effective?: DateRange;
}

export interface TimeOffRequestFilterInput {
  areaIds?: string[];
  employeeIds?: string[];
  groupIds?: string[];
  occupationIds?: string[];
  generatorIds?: string[];
  templateIds?: string[];
  reasonIds?: string[];
  custom?: FieldFilter[];
}

export interface TimeOffRequestSort {
  employee?: SortInputPriority;
  occupation?: SortInputPriority;
  area?: SortInputPriority;
  group?: SortInputPriority;
  generator?: SortInputPriority;
}

export interface TimecardSort {
  earnedDate?: SortInputPriority;
  occupation?: SortInputPriority;
  custom?: SortInput[];
}

export type UdfDocumentType =
  | 'areas'
  | 'batches'
  | 'documents'
  | 'employees'
  | 'generators'
  | 'groups'
  | 'jobs'
  | 'locations'
  | 'messages'
  | 'occupations'
  | 'organizations'
  | 'reasons'
  | 'templates'
  | 'timecards'
  | 'timeofftypes'
  | 'users'
  | string;
export interface IUdfDocumentTypes {
  [s: string]: string;
}
export type UdfType = 'boolean' | 'date' | 'float' | 'reference' | 'integer' | 'text' | 'expression';

export interface UdfFilter {
  id?: string[];
  tags?: string[];
  search?: string;
  custom?: FieldFilter[];
  documentType?: UdfDocumentType[];
  type?: string[];
  isSystem?: boolean;
  hasEffective?: boolean;
  isList?: boolean;
  displayName?: string[];
  locationId?: string[];
}
export interface UdfSort {
  documentType?: SortInputPriority;
  displayName?: SortInputPriority;
  custom?: SortInput[];
}

export interface TimeclocksFilter {
  ids?: string[];
  employeeIds?: string[];
  eventTypes?: ('in' | 'out')[];
  search?: string;
}

export interface TimeclocksSort {
  employee?: SortInputPriority;
  effective?: SortInputPriority;
}

export interface ReportFilterInput {
  employeeIds?: string[];
  occupationIds?: string[];
  batchIds?: string[];
  tags?: string[];
  areaIds?: string[];
  groupIds?: string[];
  reasonIds?: string[];
  overtimeReasonIds?: string[];
  createdBy?: string;
  search?: string;
  custom?: FieldFilter[];
  assignedEmployeeIds?: string[];
  generatorIds?: string[];
  templateIds?: string[];
  jobIds?: string[];
}

export interface ReportSecondaryFilterInput {
  custom?: FieldFilter[];
}

export interface JobInfoInput {
  jobId: string;
  dates: UTC[];
}

export interface FieldFilterInput {
  batchIds?: string[];
  batchStatus?: BatchStatusType;
  tags?: string[];
  areaIds?: string[];
  groupIds?: string[];
  occupationIds?: string[];
  reasonIds?: string[];
  templateIds?: string[];
  generatorIds?: string[];
  employeeIds?: string[];
  timecardStatus?: string[];
  custom?: FieldFilter[];
}

export interface MessageFilterInput {
  effective?: DateRange;
  levels?: MessageLevel[];
  types?: string[];
  employeeIds?: string[];
  batchIds?: string[];
  areaIds?: string[];
  groupIds?: string[];
  jobIds?: string[];
}

export interface ScheduleSort {
  area?: SortInputPriority;
  group?: SortInputPriority;
  occupation?: SortInputPriority;
  generator?: SortInputPriority;
  employee?: SortInputPriority;
  batch?: SortInputPriority;
  position?: SortInputPriority;
  custom?: SortInput[];
}

export interface FilterOptions {
  match?: JSON;
  eq?: JSON;
  range?: JSON;
  or?: JSON;
}

export interface IScheduleRuleContext {
  jobId?: string;
  earnedDate?: UTC;
  effective?: TimestampRange;
  reasonId?: string;
  areaId?: string;
  groupId?: string;
  occupationId?: string;
  templateId?: string;
  generatorId?: string;
  units?: number;
  position?: number;
  jobFields?: object;
  groupFields?: object;
  timecardFields?: object;
}

export interface IConfirmationStatus {
  organization?: string;
  location?: string;
  name?: string;
  phone?: string;
  email: string;
}

export interface ISearchResult {
  id: string;
  type: EntityType;
  code?: string;
  name?: string;
  display?: string;
  other?: Record<string, unknown>;
}

export interface FieldFilter {
  field: string;
  eq?: any;
  lt?: any;
  lte?: any;
  gt?: any;
  gte?: any;
  ne?: any;
  in?: any[];
  nin?: any[];
  match?: any;
  startsWith?: any;
  between?: FieldBetweenFilterValue;
  contains?: FieldContainsFilterValue;
  raw?: FieldRawFilterValue;
  notBetween?: FieldBetweenFilterValue;
  intersects?: FieldIntersectsFilterValue;
  notIntersects?: FieldIntersectsFilterValue;
}

export interface ScheduleFilterInput {
  areaIds?: string[];
  groupIds?: string[];
  assignedEmployeeIds?: string[];
  scheduledEmployeeIds?: string[];
  employeeIds?: string[];
  occupationIds?: string[];
  generatorIds?: string[];
  templateIds?: string[];
  reasonIds?: string[];
  jobIds?: string[];
  levels?: MessageLevel[];
  messageLevels?: MessageLevel[];
  inBatch?: boolean;
  batchIds?: string[];
  batchStatus?: BatchStatusType[];
  timecardStatus?: string[];
  timecardTestStatus?: string[];
  status?: TimecardTestStatusType;
  effective?: DateRange;
  lastRanAfter?: UTC;
  search?: string;
  timeOffStatus?: string[];
  timeOffTypes?: any[];
  availableBalance?: AvailableBalance[];
  tags?: string[];
  adjustments?: boolean;
  custom?: FieldFilter[];
}

export interface IInsertInput {
  code?: string;
  name: string;
  tags?: string[];
  fields?: Record<string, unknown>;
}

export interface IUpdateInput {
  id: string;
  code?: string;
  name?: string;
  tags?: string[];
  fields?: Record<string, unknown>;
}

export interface IUpsertInput {
  id: string;
  code?: string;
  name: string;
  tags?: string[];
  fields?: Record<string, unknown>;
}

export interface InvitationInput {
  inviteTo: string;
  userRole: string;
  employeeId?: string;
}

export interface ConfirmUserInput {
  name: string;
  avatar?: string;
  phone?: PhoneNumberInput;
  organization?: string;
  location?: string;
  timezone?: string;
  applicationTemplate?: string;
}

export interface LocationInsertInput extends IInsertInput {
  tz?: string;
  weekStart?: IWeekDay;
  timeFormat?: ITimeFormat;
  postedDate?: UTC;
  country?: string;
  state?: string;
}
export interface LocationUpdateInput extends IUpdateInput {
  tz?: string;
  weekStart?: IWeekDay;
  timeFormat?: ITimeFormat;
  postedDate?: UTC;
  country?: string;
  state?: string;
  workflow?: ITimecardWorkflow;
  timeOffRequestWorkflow?: IWorkflow;
}
export interface LocationUpsertInput extends IUpsertInput {
  tz?: string;
  weekStart?: IWeekDay;
  timeFormat?: ITimeFormat;
  postedDate?: UTC;
}

export interface AreaInsertInput extends AreaInput, IInsertInput {}
export interface AreaUpdateInput extends IUpdateInput {
  sharingScope?: SharingScope;
}
export interface AreaUpsertInput extends AreaInput, IUpsertInput {}
export interface AreaInput {
  sharingScope: SharingScope;
}
export type SharingScope = 'private' | 'location' | 'organization';

export interface GroupInsertInput extends GroupInput, IInsertInput {
  position: number;
}
export interface GroupUpdateInput extends GroupInput, IUpdateInput {
  position?: number;
}
export interface GroupUpsertInput extends GroupInput, IUpsertInput {
  position: number;
}
export interface GroupInput {
  areaId?: string;
}

export interface GroupViewUpsertInput {
  id: string;
  scheduleOptions: IScheduleViewOptions;
}

export interface EmployeeInsertInput extends EmployeeInput, IInsertInput {}
export interface EmployeeUpdateInput extends EmployeeInput, IUpdateInput {}
export interface EmployeeUpsertInput extends EmployeeInput, IUpsertInput {}
export interface EmployeeInput {
  isPayable?: boolean;
  phone?: PhoneNumberInput;
  email?: string;
}

export interface RoleInsertInput extends RoleInput, IInsertInput {}
export interface RoleUpdateInput extends RoleInput, IUpdateInput {}
export interface RoleUpsertInput extends RoleInput, IUpsertInput {}
export interface RoleInput {
  securityTokens: { [token: string]: boolean };
  workflow: UserRole;
}

export interface PhoneNumberInput {
  home?: string;
  mobile?: string;
  work?: string;
  fax?: string;
  workFax?: string;
}

export enum UserRole {
  employee,
  clerk,
  manager,
  admin,
  super_admin,
}

export interface MessageInput {
  text?: string;
  level?: MessageLevel;
  type?: string;
  effective?: UTC;
  employeeId?: string;
  jobId?: string;
  areaId?: string;
  groupId?: string;
  batchId?: string;
}

export interface MessageInsertInput extends MessageInput {}
export interface MessageUpdateInput extends MessageInput {
  id: string;
}

export interface GeneratorInsertInput extends GeneratorInput, IInsertInput {}
export interface GeneratorUpdateInput extends GeneratorInput, IUpdateInput {}
export interface GeneratorUpsertInput extends GeneratorInput, IUpsertInput {}
export interface GeneratorInput {
  cycle: (string | undefined)[]; // array of template id's
  displayStartDate: UTC;
}

export interface JobInput {
  id: string;
  generatorId?: string;
  groupId: string;
  occupationId: string;
  employeeId?: string;
  cycleOffset?: number;
  position: number;
  effective: DateRange;
  fields?: Record<string, unknown>;
}

export interface OccupationInsertInput extends OccupationInput, IInsertInput {}
export interface OccupationUpdateInput extends OccupationInput, IUpdateInput {}
export interface OccupationUpsertInput extends OccupationInput, IUpsertInput {}
export interface OccupationInput {}

export interface ReasonInput {}
export interface ReasonInsertInput extends ReasonInput, IInsertInput {
  category: ReasonCategory;
}
export interface ReasonUpdateInput extends ReasonInput, IUpdateInput {
  category?: ReasonCategory;
}
export interface ReasonUpsertInput extends ReasonInput, IUpsertInput {
  category: ReasonCategory;
}

export interface TimeOffTypeInput {}
export interface TimeOffTypeInsertInput extends TimeOffTypeInput, IInsertInput {
  trackingAmounts: boolean;
  trackingUnits: boolean;
  withdrawalReasons: string[];
  accrualReasons: string[];
}
export interface TimeOffTypeUpdateInput extends TimeOffTypeInput, IUpdateInput {
  trackingAmounts?: boolean;
  trackingUnits?: boolean;
  withdrawalReasons?: string[];
  accrualReasons?: string[];
}
export interface TimeOffTypeUpsertInput extends TimeOffTypeInput, IUpsertInput {
  trackingAmounts: boolean;
  trackingUnits: boolean;
  withdrawalReasons: string[];
  accrualReasons: string[];
}

export interface TemplateInsertInput extends TemplateInput, IInsertInput {
  template: TimecardTemplateInput[];
}
export interface TemplateUpdateInput extends TemplateInput, IUpdateInput {
  template?: TimecardTemplateInput[];
}
export interface TemplateUpsertInput extends TemplateInput, IUpsertInput {
  template: TimecardTemplateInput[];
}
export interface TemplateInput {}
export interface TimecardTemplateInput {
  start?: number;
  end?: number;
  units?: number;
  rate?: number;
  factor?: number;
  amount?: number;
  jobId?: string;
  groupId?: string;
  occupationId?: string;
  employeeId?: string;
  reasonId?: string;
  overtimeReasonId?: string;
  accountCode?: string;
}

export interface UdfInsertInput {
  id: string;
  locationId: string;
  documentType: string;
  type: string;
  isList: boolean;
  displayName: string;
}
export interface UdfUpdateInput {
  id: string;
  displayName: string;
  allowableValues?: string;
}

export interface UserInput extends IUpdateInput {
  avatar?: File;
  phone?: PhoneNumberInput;
  email?: string;
  roleId?: string;
  employeeId?: string;
}

export interface UserProfileInput {
  name?: string;
  avatar?: File;
  phone?: PhoneNumberInput;
}

export interface EntityUpsertInput {
  id: string;
  name: string;
  code?: string;
  tags?: string[];
  fields?: Record<string, unknown>;
}

export interface ViewInsertInput extends ViewInput, IInsertInput {}
export interface ViewUpdateInput extends ViewInput, IUpdateInput {}
export interface ViewUpsertInput extends ViewInput, IUpsertInput {}
export interface ViewInput {
  type?: ViewType;
  options?: IViewOptions;
  query?: string;
}

export interface BatchInsertInput extends BatchInput, IInsertInput {}
export interface BatchUpdateInput extends BatchInput, IUpdateInput {}
export interface BatchUpsertInput extends IUpsertInput {}

export interface BatchInput {
  effective: DateRange;
  areaIds?: string[];
  groupIds?: string[];
  startOfWeeks?: string[];
}

export interface TimecardInput {
  id: string;
  employeeId?: string;
  earnedDate: UTC;
  reasonId?: string;
  overtimeReasonId?: string;
  groupId?: string;
  occupationId?: string;
  jobId?: string;
  effective?: TimestampRange;
  units?: number;
  factor?: number;
  rate?: number;
  amount?: number;
  accountCode?: string;
  createdBy?: string;
  createdDate: UTC;
  tags?: string[];
  fields?: Record<string, unknown>;
  templateId?: string;
  shift: number;
}

export interface SaveTimecardInput {
  employeeId: string;
  earnedDate: UTC;
  timecards?: TimecardInput[];
}

export interface TimecardTestInsertInput {
  employeeId: string;
  effective: DateRange;
  name: string;
  code?: string;
  tags?: string[];
}

export interface TimecardTestUpdateInput {
  id: string;
  name: string;
  code?: string;
  tags?: string[];
}

export interface TimeOffAccrualInsertInput {
  employeeId: string;
  effective: string;
  timeOffTypeId: string;
  amount: number;
  units: number;
  createdBy?: string;
  createdDate?: UTC;
}

export interface TimeOffAccrualUpdateInput {
  timeOffTypeId: string;
  effective: string;
  amount: number;
  units: number;
}

export interface IMoveJobResult {
  id: string;
  position: number;
}

export interface ValidationRuleInput {
  id: string;
  schemaId: string;
  priority: number;
  parameters?: Record<string, unknown>;
  name: string;
  category: string;
}

export interface TimeOffRangeInput {
  effective?: TimestampRange;
  units: number;
  reasonId: string;
}

export interface TimeOffRequestInput {
  employeeId: string;
  effective: DateRange;
  partialDay: boolean;
  preference?: number;
  comment?: string;
  fields?: Record<string, unknown>;
  timeOffRanges: TimeOffRangeInput[];
}

export interface TimeOffRequestInsertInput extends TimeOffRequestInput {
  status?: string;
}

export interface TimeOffRequestUpdateInput extends TimeOffRequestInput {
  id: string;
}

export interface IUdtInsertInput {
  displayName: string;
}

export interface IUdtUpdateInput {
  id: string;
  displayName: string;
}

export interface IView extends INameEntity {
  type: ViewType;
  options?: IViewOptions;
  query?: string;
}
export interface IViewOptions {
  filters: ScheduleFilterInput;
  groups: string[];
  sorts: ScheduleSort;
}
export interface IScheduleViewOptions {
  filters: ScheduleFilterInput;
  groups: string[];
  sorts: ScheduleSort;
  scheduleGridOptions: IScheduleGridOptions;
}

export enum FilterComparative {
  Equals = 'eq',
  LessThan = 'lt',
  LessThanOrEquals = 'lte',
  GreaterThan = 'gt',
  GreaterThanOrEquals = 'gte',
  NotEquals = 'ne',
  In = 'in',
  NotIn = 'nin',
  // Match = 'match',
  // StartsWith = 'startsWith',
  Between = 'between',
  Contains = 'contains',
  Raw = 'raw',
  NotBetween = 'notBetween',
  // ** Below are client only comparatives
  Intersects = 'intersects',
  NotIntersects = 'notIntersects',
}

const _filterKeyMap = {
  areas: 'areaIds',
  groups: 'groupIds',
  occupations: 'occupationIds',
  assignedEmployees: 'assignedEmployeeIds',
  scheduledEmployees: 'scheduledEmployeeIds',
  employees: 'employeeIds',
  generators: 'generatorIds',
  templates: 'templateIds',
  reasons: 'reasonIds',
  batches: 'batchIds',
  levels: 'levels',
  messageLevels: 'messageLevels',
  inBatch: 'inBatch',
  batchStatus: 'batchStatus',
  timecardStatus: 'timecardStatus',
  timeOffStatus: 'timeOffStatus',
  timeOffTypes: 'timeOffTypes',
  availableBalance: 'availableBalance',
  timecardTestStatus: 'status',
  effective: 'effective',
  effectiveStart: 'effective',
  effectiveEnd: 'effective',
  lastRanAfter: 'lastRanAfter',
  tags: 'tags',
  search: 'search',
};

export function scheduleFilterInputIdFromKey(key: keyof typeof _filterKeyMap): string | undefined {
  return _filterKeyMap[key];
}

export const _udfKeyMap: { [key: UdfDocumentType]: string } = {
  areas: 'area',
  batches: 'batch',
  documents: 'document',
  employees: 'employee',
  generators: 'generator',
  groups: 'group',
  jobs: 'job',
  locations: 'location',
  messages: 'message',
  occupations: 'occupation',
  organizations: 'organization',
  reasons: 'reason',
  templates: 'template',
  timecards: 'timecard',
  timeofftypes: 'timeOffType',
  users: 'user',
};

export function lookupKeyFromUdfDocumentType(documentType: UdfDocumentType): string | undefined {
  return _udfKeyMap[documentType];
}

export interface IRouteManager {
  isLoading: boolean;
  effective?: DateRange;
  route?: string;
  path: string;
  access: boolean;
  documentType: UdfDocumentType;
  areaId?: string;
  groupId?: string;
  viewId?: string;
  allFilters?: ScheduleFilterInput;
  allGroups?: string[];
  allSorts?: ScheduleSort;
  filters?: ScheduleFilterInput;
  groups?: string[];
  sorts?: ScheduleSort;
  permittedFilters: string[];
  permittedGroups: string[];
  permittedSorts: string[];
  reasonFilterCategories: ReasonCategory[];
  allowMonth: boolean;
  allowQuarters: boolean;
  dateDurationDays: number;
  readonly title: string;
  update(): void;
  navigateTo(data?: any): void;
  createLinkFor(data?: any): string;
  createPayPlanLinkFor(data?: any): string;
  getPermittedScheduleFilterInput(filters?: ScheduleFilterInput): ScheduleFilterInput | undefined;
  getPermittedScheduleSort(sorts?: ScheduleSort): ScheduleSort | undefined;
}

export interface IPhoneNumber {
  home?: string;
  mobile?: string;
  work?: string;
  fax?: string;
  workFax?: string;
}

export interface IEmployee extends INameEntity {
  email?: string;
  isPayable?: boolean;
  phone?: IPhoneNumber;
}

export type IResponseType = 'yes' | 'no' | 'unavailable';

export interface INotification {
  id: string;
  description: string;
}

export interface IInsertResponseInput {
  type: IResponseType;
  effective: string;
  units: number;
  comment?: string;
  employeeId?: string;
  occupationId?: string;
  groupId?: string;
  templateId?: string;
  generatorId?: string;
}

export interface IResponse {
  id: string;
  type: IResponseType;
  comment?: string;
  employee: INameEntity;
  occupation?: INameEntity;
  template?: ITemplate;
  group?: INameEntity;
  generator?: INameEntity;
  units?: number;
  effective: UTC;
  createdDate?: UTC;
}

export interface IGroup extends INameEntity {
  position: number;
  area: IArea;
  scheduleOptions: IScheduleViewOptions;
}

export interface IShift {
  earnedDate: UTC;
  template?: ITemplate;
  details?: ITimecard[];
}

export interface IJob {
  id: string;
  area?: IArea;
  group: IGroup;
  generator?: IGenerator;
  occupation: IOccupation;
  employee?: IEmployee;
  cycleOffset?: number;
  effective: DateRange;
  position: number;
  fields?: Record<string, unknown>;
}

export interface IOccupation extends INameEntity {}

export type IValidTransition = { event: string; enabled: boolean };

export interface IEmployeeTimecardStatus {
  employeeId: string;
  isPayable: boolean;
  earnedDate: UTC;
  lastEditedBy?: string;
  lastEditedDate?: UTC;
  status: string;
  batch: IBatch;
  locked?: boolean;
  manual: boolean;
  allowableTransitions: IValidTransition[];
}

export type ReportType = 'timecardDetail' | 'timecardSummary' | 'schedule' | 'employee' | 'export';

export interface IReport extends INameEntity {
  path: string;
  reportId: string;
  type: ReportType;
}

export interface IReportInput {
  reportId: string;
  type: ReportType;
  name: string;
  title?: string;
}

export type ReportExtension = '.pdf' | '.xlsx';
export type ReportOutput = 'PDF' | 'Excel';
export type ReportOrientation = 'Portrait' | 'Landscape';
export type ReportPaperSize = 'Letter' | 'Legal' | 'Tabloid' | 'A1' | 'A2' | 'A3' | 'A4' | 'A5' | 'A6' | 'ExtraWide' | 'SuperWide';

export interface IReportFormatInput {
  output?: ReportOutput;
  orientation?: ReportOrientation;
  paperSize?: ReportPaperSize;
}

export interface ITemplate extends INameEntity {
  template: ITimecardTemplate[];
  locked: boolean;
}
export interface ITimecardTemplate {
  start?: number;
  end?: number;
  units?: number;
  rate?: number;
  factor?: number;
  amount?: number;
  group?: IGroup;
  occupation?: IOccupation;
  employee?: IEmployee;
  reason?: IReason;
  overtimeReason?: IReason;
  accountCode?: string;
}

export interface ISchedule {
  id: string;
  schedule: IShift[];
  group: IGroup;
  generator?: IGenerator;
  occupation: IOccupation;
  employee?: IEmployee;
  cycleOffset?: number;
  position: number;
  effective: DateRange;
  fields?: Record<string, any>;
}

export interface ISchedulingToolSchedule {
  id: string;
  employee?: IEmployee;
  schedule: IShift[];
}

type TimeclockEvent = 'in' | 'out';

export interface ITimeclock {
  id: string;
  employeeId: string;
  effective: UTC;
  eventType: TimeclockEvent;
  location?: string;
  lastEditedDate?: UTC;
  lastEditedBy?: string;
  comment?: string;
  minutes: number;
}

interface IScheduleSearchFieldResult {
  id: string;
  name: string;
  code?: string;
  search: string[];
}

export interface IScheduleSearchResult {
  job: { id: string };
  earnedDate: UTC;
  employee?: IScheduleSearchFieldResult;
  reason?: IScheduleSearchFieldResult;
  overtimeReason?: IScheduleSearchFieldResult;
  occupation?: IScheduleSearchFieldResult;
  group?: IScheduleSearchFieldResult;
  area?: IScheduleSearchFieldResult;
  template?: IScheduleSearchFieldResult;
  generator?: IScheduleSearchFieldResult;
}

export interface IJobSearchResult {
  id?: string;
  group?: IScheduleSearchFieldResult;
  occupation?: IScheduleSearchFieldResult;
  generator?: IScheduleSearchFieldResult;
  employee?: IScheduleSearchFieldResult;
  isQualified?: boolean;
}

export interface ISavedSchedule {
  id: string;
  schedule: IShift[];
}

export interface IUdf {
  id: string;
  documentType: UdfDocumentType;
  type: UdfType;
  name: string;
  isSystem: boolean;
  isList: boolean;
  hasEffective: boolean;
  displayName: string;
  allowableValues?: string;
  location: ILocation;
}

export interface IGenerator extends INameEntity {
  cycle: (ITemplate | undefined)[];
  locked: boolean;
  displayStartDate?: UTC;
}

export interface ITimecard {
  id: string;
  earnedDate: UTC;
  effective?: TimestampRange;
  accountCode?: string;
  units?: number;
  factor?: number;
  rate?: number;
  amount?: number;
  template?: ITemplate;
  createdBy?: string;
  createdDate?: UTC;
  tags?: string[];
  job?: IJob;
  employee?: IEmployee;
  reason?: IReason;
  overtimeReason?: IReason;
  generated?: IGenerated;
  group?: IGroup;
  batch?: IBatch;
  fields?: Record<string, unknown>;
  occupation?: IOccupation;
  lastEditedBy?: string;
  lastEditedDate?: UTC;
  /* Properties not on ITimecard normally, but used by Pay models currently */
  start?: UTC;
  end?: UTC;
  shift: number;
  timeOffStatus?: string;
  hideDeleteIcon?: boolean;
  timecardType?: 'r' | 'c' | undefined;
}

export type TimecardTestStatusType = 'ok' | 'error';
export enum TimecardTestStatus {
  ok = 'ok',
  error = 'error',
}

export interface ITimecardTest extends INameEntity {
  employee: IEmployee;
  effective: DateRange;
  area?: IArea;
  group?: IGroup;
  occupation?: IOccupation;
  generator?: IGenerator;
  total: number;
  difference: number;
  status: TimecardTestStatusType;
  lastRan: UTC;
  lastEditedBy: string;
  originalTimecards?: ITimecard[];
  actualTimecards?: ITimecard[];
  details: ITimecardTestDetail[];
  isSelected: boolean;
}

export interface ITimecardTestDetail {
  units: number;
  rate: number;
  factor: number;
  amount: number;
  difference: number;
  status: TimecardTestStatusType;
  reason: IReason;
  overtimeReason?: IReason;
}

export interface IGenerated {
  job: IJob;
  template?: ITemplate;
  earnedDate: UTC;
  regenerate: boolean;
}

export interface INameValue {
  name: string;
  value: number;
}

export interface IBatchEmployeeGroup {
  id: string;
  name: string;
  errors: number;
  gross: number;
  otherPay: number;
  reasons: INameValue[];
}

export interface IBatchEmployeeTimecard {
  id: string;
  groupByKeys: string[];
  employee: string;
  errors: number;
  gross?: number;
  status: string;
  otherPay: number;
  timecardType: 'r' | 'c' | undefined;
  reasons: INameValue[];
}

export interface IBatchEmployeeTimecardResult {
  totalCount: number;
  cursor?: string;
  columns: string[];
  gross: number;
  otherPay: number;
  errors: number;
  reasons: INameValue[];
  groups: IBatchEmployeeGroup[];
  results: IBatchEmployeeTimecard[];
}

export interface IEmployeeTimecard {
  earnedDate: UTC;
  employee: IEmployee;
  batch: IBatch;
  lastEditedBy: string;
  lastEditedDate: UTC;
  status: string;
}

export interface IBatchTimecard {
  id: string;
  code?: string;
  name?: string;
  effective?: DateRange;
  status?: BatchStatusType;
  timecardCount?: number;
  gross?: number;
  errors?: number;
  createdDate?: UTC;
  createdBy?: string;
}

export interface IOtherPayment {
  id: string;
  earnedDate?: UTC;
  rate?: number;
  factor?: number;
  units?: number;
  amount?: number;
  accountCode?: string;
  reason?: IReason;
  batch?: IBatch;
  occupation?: IOccupation;
  group?: IGroup;
  template?: ITemplate;
  createdBy?: string;
  createdDate?: UTC;
  hideDeleteIcon: boolean;
}

export interface IEmployeeListItem extends INameEntity {
  warning?: number;
  error?: number;
  phone?: IPhoneNumber;
}

export interface IEmployeeSummary {
  id: string;
  name: string;
  warning: number;
  error: number;
  info: number;
  isSelected: boolean;
  saving: boolean;
}

export type TimecardGroupingType = 'area' | 'employee';
export type BatchStatusType = 'closed' | 'exported' | 'not_in_batch';
export type ExportStatusType = 'not_exported' | 'exported';

export interface BatchFilterInput {
  effective?: DateRange;
  ids?: string[];
  code?: string;
  name?: string;
  search?: string;
}

export enum TimecardStatus {
  Outstanding = 'Outstanding',
  Pending = 'Pending',
  Closed = 'Closed',
}

export enum BatchStatus {
  NotInBatch = 'Not In Batch',
  Closed = 'Closed',
  Exported = 'Exported',
}

export function toBatchStatus(status: BatchStatusType): BatchStatus | undefined {
  switch (status) {
    case 'not_in_batch':
      return BatchStatus.NotInBatch;
    case 'closed':
      return BatchStatus.Closed;
    case 'exported':
      return BatchStatus.Exported;
  }
}

export function toBatchStatusType(status: BatchStatus | undefined): BatchStatusType | undefined {
  switch (status) {
    case BatchStatus.NotInBatch:
      return 'not_in_batch';
    case BatchStatus.Closed:
      return 'closed';
    case BatchStatus.Exported:
      return 'exported';
    default:
      return undefined;
  }
}

export enum ExportStatus {
  NotExported = 'Processing',
  Exported = 'Generated',
}

export function toExportStatus(status: ExportStatusType): ExportStatus | undefined {
  switch (status) {
    case 'not_exported':
      return ExportStatus.NotExported;
    case 'exported':
      return ExportStatus.Exported;
  }
}

export function toExportStatusType(status: ExportStatus | undefined): ExportStatusType | undefined {
  switch (status) {
    case ExportStatus.NotExported:
      return 'not_exported';
    case ExportStatus.Exported:
      return 'exported';
    default:
      return undefined;
  }
}

export enum GroupTimecardsBy {
  Employee = 'Employee',
  Area = 'Area',
  Batch = 'Batch',
}

export interface ISAMLIdentityProvider {
  entityID: string;
  replyURL: string;
  metadataURL?: string;
}

export interface IAuthenticationOptions {
  saml: boolean;
  usernamePassword: boolean;
  apple: boolean;
  mfa: boolean;
  provider?: ISAMLIdentityProvider;
}

export interface IMessage {
  id: string;
  text?: string;
  level?: MessageLevel;
  type?: string;
  effective?: UTC;
  employee?: IEmployee;
  jobId?: string;
  area?: IArea;
  group?: IGroup;
  batch?: IBatch;
  createdDate?: UTC;
  createdBy?: string;
}

export interface IMessageLevelCount {
  level: MessageLevel;
  count: number;
}

export interface IReason extends INameEntity {
  category: ReasonCategory;
}

export interface ITimeOffTypeTotals {
  totalAccruals: number;
  totalWithdrawals: number;
  balance: number;
  plannedWithdrawals: number;
  usedWithdrawals: number;
}

export interface ITimeOffTypeTransaction {
  id: string;
  effective?: UTC;
  units?: number;
  amount?: number;
  type: string;
  source?: string;
  messages?: IMessage[];
}

export interface ITimeOffType extends INameEntity {
  trackingAmounts: boolean;
  trackingUnits: boolean;
  withdrawalReasons?: INameEntity[];
  accrualReasons?: INameEntity[];
  messages?: IMessage[];
  transactions?: ITimeOffTypeTransaction[];
  totals?: {
    byUnits?: ITimeOffTypeTotals;
    byAmount?: ITimeOffTypeTotals;
  }[];
}

export class TimeOffType implements ITimeOffType {
  id!: string;
  name!: string;
  trackingAmounts!: boolean;
  trackingUnits!: boolean;
  withdrawalReasons?: INameEntity[];
  accrualReasons?: INameEntity[];
  messages?: IMessage[];
  transactions?: ITimeOffTypeTransaction[];
  totals?: {
    byUnits?: ITimeOffTypeTotals;
    byAmount?: ITimeOffTypeTotals;
  }[];

  static fromJson(json: any): TimeOffType {
    const tot: TimeOffType = new TimeOffType();
    tot.id = json.id;
    tot.name = json.name;
    tot.trackingAmounts = json.trackingAmounts;
    tot.trackingUnits = json.trackingUnits;
    tot.withdrawalReasons = json.withdrawalReasons;
    tot.accrualReasons = json.accrualReasons;
    tot.totals = json.totals;
    tot.messages = json.messages;
    tot.transactions = json.transactions?.[0]?.transactions;
    return tot;
  }
}

export interface ITimeOffAccrual {
  id: string;
  employee: IEmployee;
  effective: DateRange;
  timeOffType: ITimeOffType;
  createdBy?: string;
  createdDate?: UTC;
  units: number;
  amount: number;
}

export interface ITimeOffTypeSummary {
  id: string;
  name: string;
  trackingAmounts: boolean;
  trackingUnits: boolean;
  withdrawalReasons: INameEntity[];
  accrualReasons: INameEntity[];
  earned: ITimeOffTypeSummaryEntry;
  used: ITimeOffTypeSummaryEntry;
  planned: ITimeOffTypeSummaryEntry;
  available: ITimeOffTypeSummaryEntry;
  requestedOnHold: ITimeOffTypeSummaryEntry;
  resetWith(timeOffType: ITimeOffType): void;
}

export interface ITimeOffTypeSummaryEntry {
  hours: number;
  amount: number;
}

export class TimeOffTypeSummary implements ITimeOffTypeSummary {
  id: string;
  name: string;
  trackingAmounts: boolean;
  trackingUnits: boolean;
  withdrawalReasons: INameEntity[];
  accrualReasons: INameEntity[];

  earned!: ITimeOffTypeSummaryEntry;
  used!: ITimeOffTypeSummaryEntry;
  planned!: ITimeOffTypeSummaryEntry;
  available!: ITimeOffTypeSummaryEntry;
  requestedOnHold!: ITimeOffTypeSummaryEntry;

  constructor(timeOffType: ITimeOffType) {
    this.id = timeOffType.id;
    this.name = timeOffType.name;
    this.trackingAmounts = !!timeOffType.trackingAmounts;
    this.trackingUnits = !!timeOffType.trackingUnits;
    this.withdrawalReasons = timeOffType.withdrawalReasons || [];
    this.accrualReasons = timeOffType.accrualReasons || [];
    this.resetWith(timeOffType);
  }

  resetWith(timeOffType: ITimeOffType) {
    this.earned = {
      hours: timeOffType.totals?.[0]?.byUnits?.totalAccruals || 0,
      amount: timeOffType.totals?.[0]?.byAmount?.totalAccruals || 0,
    };
    this.used = {
      hours: timeOffType.totals?.[0]?.byUnits?.usedWithdrawals || 0,
      amount: timeOffType.totals?.[0]?.byAmount?.usedWithdrawals || 0,
    };
    this.planned = {
      hours: timeOffType.totals?.[0]?.byUnits?.plannedWithdrawals || 0,
      amount: timeOffType.totals?.[0]?.byAmount?.plannedWithdrawals || 0,
    };
    this.available = {
      hours: timeOffType.totals?.[0]?.byUnits?.balance || 0,
      amount: timeOffType.totals?.[0]?.byAmount?.balance || 0,
    };
    this.requestedOnHold = { hours: 0, amount: 0 };
  }
}

export interface ITimeOffRange {
  effective?: TimestampRange;
  units: number;
  reason: IReason;
}

export interface ITimeOffRequest {
  id: string;
  employee: IEmployee;
  effective: DateRange;
  status?: string;
  partialDay: boolean;
  comment?: string;
  fields?: object;
  timeOffRanges: ITimeOffRange[];
  createdBy: string;
  createdDate: UTC;
  preference?: number;
  job?: IJob;
  lastEditedBy?: string;
  lastEditedDate?: UTC;
}

export interface ITimeOffRequestStatus {
  id: string;
  status: string;
  allowableTransitions?: IValidTimeOffRequestTransition[];
}
export interface IValidTimeOffRequestTransition {
  event: string;
  enabled: boolean;
}

export interface ITimecardReason extends IReason {
  category: TimecardReasonCategory;
}

export interface ITimecardOvertimeReason extends IReason {
  category: 'overtime';
}

export interface ITimecardSummaryStatus {
  status: string;
  total: number;
}

export interface ITimecardSummary {
  notInBatch: number;
  inBatch: number;
  total: number;
  errors: number;
  totalEmployees: number;
  statuses: ITimecardSummaryStatus[];
}

export type MessageLevel = 'info' | 'success' | 'warning' | 'error';
export const ReasonCategoriesList = ['worked', 'absence', 'break', 'overtime', 'premium', 'other'] as const;
export const ResponsesList = ['no', 'unavailable'] as const;
export type ReasonCategory = (typeof ReasonCategoriesList)[number];

export type TimecardReasonCategory = 'worked' | 'absence' | 'break' | 'premium';
export enum MessageLevels {
  Info = 'info',
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
}
export enum ReasonCategories {
  Worked = 'worked',
  Absence = 'absence',
  Break = 'break',
  Overtime = 'overtime',
  Premium = 'premium',
  Other = 'other',
}

export type AvailableBalance = 'greaterThanZero' | 'lessThanZero';
export enum AvailableBalances {
  greaterThanZero = 'greaterThanZero',
  lessThanZero = 'lessThanZero',
}

export type eValidationParameterType = 'number' | 'string' | 'reference' | 'boolean' | 'object' | 'udf' | 'tag' | 'date';

export interface IValidationRuleSchema {
  schemaId: string;
  priority: number;
  parameters?: IValidationParameters;
  name: string;
  category: string;
}

export interface IValidationRule extends IValidationRuleSchema {
  id: string;
  isNew?: boolean;
}

export interface IValidationRuleSet {
  schema: IValidationRuleSchema[];
  rules: IValidationRule[];
}

export interface IValidationParameters {
  [s: string]: IValidationParameter;
}

export interface IValidationParameter {
  name: string;
  type: eValidationParameterType;
  typeOptions?: any;
  columnType: eColumnType;
  multiSelect?: boolean;
  order: number;
  value: any;
}

export interface IUser extends INameEntity {
  organization: IOrganization;
  location: ILocation;
  email: string;
  phone?: IPhoneNumber;
  avatar?: string;
  lastEditedBy?: string;
  userSecurity: IUserSecurity;
  tier: Tier;
}

export interface IRole extends INameEntity {
  location: ILocation;
  isSystem: boolean;
  isSuperAdmin: boolean;
  securityTokens: { [token: string]: boolean };
  workflow: UserRole;
  lastEditedBy?: string;
}

export interface IApiKey {
  id: string;
  name: string;
  tz: string;
  country: string;
  state: string;
  right: string;
}

export interface IUserSecurity {
  scopeIds: string[];
  role: IRole;
}

export type Tier = 'starter' | 'professional' | 'enterprise';

export interface ISecurityToken {
  token: string;
  description: string;
  minimumTier: Tier;
  path: string;
  module?: string;
  // defaultRoles: UserRole[]
}

export interface IUdt {
  id: string;
  locationId: string;
  name: string;
  displayName: string;
}

export const defaultReturnSchema = 'id';
export type Scope = IOrganization | ILocation | IArea;

export const passwordRegEx = validator(
  'regex',
  new RegExp(
    /^(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z])(?=\D*\d)(?=.*[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`-])[A-Za-z\d^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]{8,}$/,
  ),
  error(
    'A valid password requires a minimum of 8 characters, an uppercase letter, a lowercase letter, a number and a special character e.g., ^ $ * . [ ] { } ( ) ? - " ! @ # % & /  , > < \' : ; | _ ~ ` - "',
  ),
);

export interface IWorkSightThemeColors {
  primary?: SimplePaletteColorOptions | Partial<any>;
  secondary?: SimplePaletteColorOptions | Partial<any>;
  success?: SimplePaletteColorOptions | Partial<any>;
  error?: SimplePaletteColorOptions | Partial<any>;
  info?: SimplePaletteColorOptions | Partial<any>;
  warning?: SimplePaletteColorOptions | Partial<any>;
  highlight?: SimplePaletteColorOptions | Partial<any>;
  neutral?: SimplePaletteColorOptions | Partial<any>;
  neutralLight?: SimplePaletteColorOptions | Partial<any>;
  navBarPrimary?: SimplePaletteColorOptions | Partial<any>;
  navBarSecondary?: SimplePaletteColorOptions | Partial<any>;
  dialogPrimary?: SimplePaletteColorOptions | Partial<any>;
  gridSelect?: SimplePaletteColorOptions | Partial<any>;
  tonalOffset?: number;
}

export type ThemeType = 'default' | 'shocking';

export const themeTypePalettes: { [s: string]: IWorkSightThemeColors } = {
  /****** Default ******/
  default: {
    primary: {
      main: '#153D4B',
    },
    secondary: {
      main: '#DD9719',
    },
    success: {
      main: '#669639',
    },
    error: {
      main: '#cc0000',
    },
    info: {
      main: '#1976D2',
    },
    warning: {
      main: '#FFA000',
    },
    highlight: {
      main: '#f7fcd4',
    },
    neutral: {
      // main: '#796D6C',
      // main: '#655958',
      main: '#241B1A',
    },
    neutralLight: {
      light: '#efefef',
      main: '#e0e0e0',
    },
    navBarPrimary: {
      main: '#F7F6F5',
    },
    navBarSecondary: {
      light: '#D0D8DB',
      main: '#95A7AE',
    },
    dialogPrimary: {
      light: '#3aa6c3',
      main: '#14809D',
      dark: '#0E5A6E',
    },
    gridSelect: {
      main: '#D0DEE8',
    },
    tonalOffset: 0.25,
  },
  /****** Shocking ******/
  shocking: {
    primary: {
      main: '#303f9f',
    },
    secondary: {
      main: '#4caf50',
    },
    success: {
      main: '#669639',
    },
    error: {
      main: '#cc0000',
    },
    info: {
      main: '#1976D2',
    },
    warning: {
      main: '#FFA000',
    },
    highlight: {
      main: '#f7fcd4',
    },
    neutral: {
      main: '#333333',
    },
    neutralLight: {
      light: '#eeeeee',
      main: '#dddddd',
    },
    navBarPrimary: {
      main: '#e0e0e0',
    },
    navBarSecondary: {
      light: '#cfcfcf',
      main: '#9e9e9e',
    },
    dialogPrimary: {
      light: '#ae52d4',
      main: '#7b1fa2',
      dark: '#0E5A6E',
    },
    gridSelect: {
      main: '#9AC3E2',
    },
    tonalOffset: 0.25,
  },
};

export function getTimeOffStatusColors(theme: Theme) {
  return {
    requested: theme.palette.secondary.main,
    approved: theme.palette.info.main,
    onhold: theme.palette.warning.light,
    declined: theme.palette.error.main,
    cancelled: '#796d6c',
    published: theme.palette.primary.main,
    accepted: theme.palette.success.main,
  };
}

export enum FilterModes {
  None = 'None',
  All = 'All',
  Any = 'Any',
}

export const reasonCategorySearcher = arraySearch(Object.values(ReasonCategories), (item?: ReasonCategory) => item || '');
export const batchStatusSearcher = arraySearch(
  Object.values(BatchStatus),
  (item: any) => (typeof item === 'string' && capitalCase(item)) || '',
);
export const timecardTestStatusSearcher = arraySearch(Object.values(TimecardTestStatus), (item: any) => item || '');
export const groupTimecardsBySearcher = arraySearch(Object.keys(GroupTimecardsBy), (item?: any) => item || '');

export const numberColumn = { type: 'number', options: { fixed: true, decimals: 2, thousandSeparator: true } };

export type ColumnValue =
  | number[]
  | Date[]
  | string[]
  | boolean[]
  | number
  | Date
  | string
  | boolean
  | '*'
  | '«empty»'
  | Record<string, unknown>;
export type ITableRow<T> = { [P in keyof T]: ColumnValue } & { specificity?: number };
export type eTableType = 'expression' | 'number' | 'string' | 'reference' | 'boolean' | 'object' | 'udf' | 'tag' | 'date';
export type eColumnType = 'match' | 'result';

export interface ITableSchemaColumn {
  name: string;
  type: eTableType;
  typeOptions?: any;
  weight?: number;
  columnType: eColumnType;
  multiSelect?: boolean;
  width?: string;
  order: number;
}

export interface ITableSchemaDefn<T> {
  id: string;
  name: string;
  schema: ITableSchema<T>;
}

export type ITableSchema<T> = {
  [P in keyof T]: ITableSchemaColumn;
};

export interface IRuleSetTable {
  schemaDefn: ITableSchemaDefn<any>;
  rows: ITableRow<any>[];
}

export interface IDocument extends INameEntity {
  type: string;
  scopeId: string;
  locationId: string;
}

export type ScheduleMode = 'Job' | 'Employee';

export interface ITimecardStatusFilter {
  adjustments?: boolean;
}

export interface IScheduleGridOptions {
  assignedEmployeeColumn: boolean;
  jobColumn: boolean;
  jobCellGenerator: boolean;
  shiftCellTemplate: boolean;
  shiftCellShiftPattern: boolean;
  shiftCellTimes: boolean;
  shiftCellOccupations: boolean;
  shiftCellReasons: boolean;
  shiftCellBreaks: boolean;
  shiftCellTimeOffRequests: boolean;
  shiftCellExceptionsOnly: boolean;
}

export type MultipleType = '«multiple»';

export function getMultipleObject(): any {
  return {
    id: guid(),
    name: '«multiple»',
    code: '«multiple»',
  };
}

export function getMultiple(): MultipleType {
  return '«multiple»';
}

export function isMultiple(value: any): boolean {
  const multipleStr = getMultiple();
  return value === multipleStr || value?.name === multipleStr;
}
